/**
 * Local User Service
 * 
 * Manages LocalUsers for device local-control authentication.
 * Automatically syncs users to devices via MQTT after create/update/delete.
 */

import { generateClient } from 'aws-amplify/data';
import type { Schema } from '../../amplify/data/resource';
import bcrypt from 'bcryptjs';

const client = generateClient<Schema>();

export interface LocalUserInput {
  username: string;
  email: string;
  password: string; // Plain text - will be hashed
  clientId: string;
  role: 'admin' | 'operator' | 'viewer';
}

export interface LocalUser {
  id: string;
  username: string;
  email: string;
  passwordHash: string;
  clientId: string;
  role: string;
  isActive: boolean;
  createdBy: string;
  createdAt?: string;
  updatedAt?: string;
}

/**
 * Create a new LocalUser and sync to devices
 */
export async function createLocalUser(input: LocalUserInput, createdBy: string): Promise<LocalUser> {
  try {
    console.log('Creating LocalUser:', input.username);
    
    // Hash the password with bcrypt (same as device expects)
    const passwordHash = await bcrypt.hash(input.password, 10);
    
    // Create the user in DynamoDB
    const { data: user, errors } = await client.models.LocalUser.create({
      username: input.username,
      email: input.email,
      passwordHash,
      clientId: input.clientId,
      role: input.role,
      isActive: true,
      createdBy,
    });
    
    if (errors) {
      console.error('Error creating LocalUser:', errors);
      throw new Error(errors.map(e => e.message).join(', '));
    }
    
    if (!user) {
      throw new Error('No user returned from create operation');
    }
    
    console.log('LocalUser created:', user.id);
    
    // Automatically sync to devices
    await syncUserToDevices({
      userId: user.id,
      username: user.username,
      email: user.email,
      passwordHash: user.passwordHash,
      clientId: user.clientId,
      role: user.role,
      isActive: user.isActive ?? true,
      action: 'create',
    });
    
    return user as LocalUser;
  } catch (error) {
    console.error('Failed to create LocalUser:', error);
    throw error;
  }
}

/**
 * Update a LocalUser and sync to devices
 */
export async function updateLocalUser(
  id: string,
  updates: Partial<LocalUserInput>
): Promise<LocalUser> {
  try {
    console.log('Updating LocalUser:', id);
    
    // If password is being updated, hash it
    let passwordHash: string | undefined;
    if (updates.password) {
      passwordHash = await bcrypt.hash(updates.password, 10);
    }
    
    // Update the user in DynamoDB
    const { data: user, errors } = await client.models.LocalUser.update({
      id,
      ...(updates.username && { username: updates.username }),
      ...(updates.email && { email: updates.email }),
      ...(passwordHash && { passwordHash }),
      ...(updates.role && { role: updates.role }),
    });
    
    if (errors) {
      console.error('Error updating LocalUser:', errors);
      throw new Error(errors.map(e => e.message).join(', '));
    }
    
    if (!user) {
      throw new Error('No user returned from update operation');
    }
    
    console.log('LocalUser updated:', user.id);
    
    // Automatically sync to devices
    await syncUserToDevices({
      userId: user.id,
      username: user.username,
      email: user.email,
      passwordHash: user.passwordHash,
      clientId: user.clientId,
      role: user.role,
      isActive: user.isActive ?? true,
      action: 'update',
    });
    
    return user as LocalUser;
  } catch (error) {
    console.error('Failed to update LocalUser:', error);
    throw error;
  }
}

/**
 * Deactivate a LocalUser and sync to devices
 */
export async function deactivateLocalUser(id: string): Promise<void> {
  try {
    console.log('Deactivating LocalUser:', id);
    
    // Get the user first to get their details for sync
    const { data: user } = await client.models.LocalUser.get({ id });
    
    if (!user) {
      throw new Error('User not found');
    }
    
    // Update to inactive
    const { errors } = await client.models.LocalUser.update({
      id,
      isActive: false,
    });
    
    if (errors) {
      console.error('Error deactivating LocalUser:', errors);
      throw new Error(errors.map(e => e.message).join(', '));
    }
    
    console.log('LocalUser deactivated:', id);
    
    // Sync deactivation to devices
    await syncUserToDevices({
      userId: user.id,
      username: user.username,
      email: user.email,
      passwordHash: user.passwordHash,
      clientId: user.clientId,
      role: user.role,
      isActive: false,
      action: 'delete',
    });
  } catch (error) {
    console.error('Failed to deactivate LocalUser:', error);
    throw error;
  }
}

/**
 * Delete a LocalUser and sync to devices
 */
export async function deleteLocalUser(id: string): Promise<void> {
  try {
    console.log('Deleting LocalUser:', id);
    
    // Get the user first to get their details for sync
    const { data: user } = await client.models.LocalUser.get({ id });
    
    if (!user) {
      throw new Error('User not found');
    }
    
    // Sync deletion to devices first
    await syncUserToDevices({
      userId: user.id,
      username: user.username,
      email: user.email,
      passwordHash: user.passwordHash,
      clientId: user.clientId,
      role: user.role,
      isActive: false,
      action: 'delete',
    });
    
    // Then delete from DynamoDB
    const { errors } = await client.models.LocalUser.delete({ id });
    
    if (errors) {
      console.error('Error deleting LocalUser:', errors);
      throw new Error(errors.map(e => e.message).join(', '));
    }
    
    console.log('LocalUser deleted:', id);
  } catch (error) {
    console.error('Failed to delete LocalUser:', error);
    throw error;
  }
}

/**
 * List LocalUsers for a client
 */
export async function listLocalUsers(clientId: string): Promise<LocalUser[]> {
  try {
    console.log('Listing LocalUsers for client:', clientId);
    
    const { data: users, errors } = await client.models.LocalUser.listLocalUserByClientIdAndCreatedAt({
      clientId,
    });
    
    if (errors) {
      console.error('Error listing LocalUsers:', errors);
      throw new Error(errors.map(e => e.message).join(', '));
    }
    
    return (users || []) as LocalUser[];
  } catch (error) {
    console.error('Failed to list LocalUsers:', error);
    throw error;
  }
}

/**
 * Get a single LocalUser by ID
 */
export async function getLocalUser(id: string): Promise<LocalUser | null> {
  try {
    const { data: user, errors } = await client.models.LocalUser.get({ id });
    
    if (errors) {
      console.error('Error getting LocalUser:', errors);
      throw new Error(errors.map(e => e.message).join(', '));
    }
    
    return user as LocalUser | null;
  } catch (error) {
    console.error('Failed to get LocalUser:', error);
    throw error;
  }
}

/**
 * Sync user to devices via MQTT
 * This is called automatically after create/update/delete operations
 */
async function syncUserToDevices(params: {
  userId: string;
  username: string;
  email: string;
  passwordHash: string;
  clientId: string;
  role: string;
  isActive: boolean;
  action: 'create' | 'update' | 'delete';
}): Promise<void> {
  try {
    console.log(`Syncing user ${params.username} to devices (${params.action})`);
    
    // Call the syncLocalUserToDevices mutation
    const { data, errors } = await client.mutations.syncLocalUserToDevices({
      userId: params.userId,
      username: params.username,
      email: params.email,
      passwordHash: params.passwordHash,
      clientId: params.clientId,
      role: params.role,
      isActive: params.isActive,
      action: params.action,
    });
    
    if (errors) {
      console.error('Error syncing user to devices:', errors);
      throw new Error(errors.map(e => e.message).join(', '));
    }
    
    console.log('User sync result:', data);
  } catch (error) {
    console.error('Failed to sync user to devices:', error);
    // Don't throw - user is created/updated, sync can be retried
    console.warn('User operation succeeded but sync failed. User will sync on next device connection.');
  }
}

export default {
  createLocalUser,
  updateLocalUser,
  deactivateLocalUser,
  deleteLocalUser,
  listLocalUsers,
  getLocalUser,
};
